import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiPlus, FiTrash2, FiChevronUp, FiFileText, FiTruck, FiAlertCircle, FiClipboard, FiCopy, FiNavigation, FiUpload } from 'react-icons/fi';
import api from '../../services/api';
import AccountingTab from '../truck/AccountingTab';
import DocCenterTab from '../truck/DocCenterTab';
import { getTrackingUrl } from '../../config/carrierTracking';
import StatusSelector from '../../components/common/StatusSelector';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';
import ImportShipmentModal from '../../components/ImportShipmentModal';

const AirImportDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [hawbs, setHawbs] = useState([]);
  const [expandedHawbs, setExpandedHawbs] = useState({});
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [awbUploading, setAwbUploading] = useState(false);
  const awbFileRef = useRef(null);
  const initialLoadDone = useRef(false);
  const toolsRef = useRef(null);

  const emptyMawb = {
    file_no: '', mawb_no: '', office: '', awb_type: 'NORMAL', post_date: new Date().toISOString().split('T')[0],
    oversea_agent: '', carrier: '', awb_acct_carrier: '', co_loader: '', trucker: '', op: '',
    direct_master: false,
    departure: '', departure_date_time: '', destination: '', arrival_date_time: '',
    flight_no: '', connecting_flight: '', freight_location: '', storage_start_date: '',
    package_count: '', pkg_type: 'CARTON(S)',
    gross_weight_kg: '', gross_weight_lb: '',
    chargeable_weight_kg: '', chargeable_weight_lb: '',
    volume_weight_kg: '', measurement_cbm: '',
    freight: '', incoterms: '', service_term_from: 'AIRPORT', service_term_to: 'AIRPORT',
    stackable: true, cargo_type: 'GENERAL CARGO', business_referred_by: '',
    status: 'Pending', memo: '',
  };

  const [formData, setFormData] = useState(emptyMawb);
  const [shipmentId, setShipmentId] = useState(id || null);

  useEffect(() => {
    if (id) { loadShipment(id); }
    else { setTimeout(() => { initialLoadDone.current = true; }, 0); }
  }, [id]);

  // Track dirty state from any formData change
  useEffect(() => {
    if (initialLoadDone.current) setIsDirty(true);
  }, [formData]);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isNew && !isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isNew, isDirty]);

  const loadShipment = async (sid) => {
    try {
      const res = await api.get(`/air-import/${sid}`);
      const d = res.data;
      const mapped = {};
      Object.keys(emptyMawb).forEach(k => {
        if (['post_date','storage_start_date'].includes(k)) mapped[k] = d[k] ? d[k].split('T')[0] : '';
        else if (['departure_date_time','arrival_date_time'].includes(k)) mapped[k] = d[k] || '';
        else if (typeof emptyMawb[k] === 'boolean') mapped[k] = !!d[k];
        else mapped[k] = d[k] || '';
      });
      setFormData(mapped); setShipmentId(d.id);
      if (d.hawbs?.length > 0) {
        setHawbs(d.hawbs);
        const ex = {}; d.hawbs.forEach(h => { ex[h.id] = true; }); setExpandedHawbs(ex);
      }
    } catch (error) { toast.error('Failed to load shipment'); navigate('/air-import'); }
    finally { setLoading(false); setTimeout(() => { initialLoadDone.current = true; }, 0); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isNew) {
        const { file_no, ...createData } = formData;
        const res = await api.post('/air-import', createData);
        const data = res.data;
        setShipmentId(data.id);
        setFormData(prev => ({ ...prev, file_no: data.file_no }));
        setIsDirty(false);
        toast.success(`Shipment ${data.file_no} created`);
        navigate(`/air-import/${data.id}`, { replace: true });
      } else {
        const { file_no, ...updateData } = formData;
        await api.put(`/air-import/${shipmentId}`, updateData);
        setIsDirty(false);
        toast.success('Shipment saved');
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // HAWB CRUD
  const addHawb = async () => {
    try {
      const res = await api.post(`/air-import/${shipmentId}/hawb`, {});
      setHawbs(prev => [...prev, res.data]);
      setExpandedHawbs(prev => ({ ...prev, [res.data.id]: true }));
      toast.success('HAWB added');
    } catch (error) { toast.error('Failed to add HAWB'); }
  };

  const handleHawbChange = (hid, e) => {
    const { name, value, type, checked } = e.target;
    setHawbs(prev => prev.map(h => h.id === hid ? { ...h, [name]: type === 'checkbox' ? checked : value } : h));
  };

  const setHawbField = (hid, name, value) => {
    setHawbs(prev => prev.map(h => h.id === hid ? { ...h, [name]: value } : h));
  };

  const saveHawb = async (hid) => {
    const hawb = hawbs.find(h => h.id === hid);
    if (!hawb) return;
    try {
      const { id: _, shipment_id, created_at, updated_at, ...ud } = hawb;
      await api.put(`/air-import/hawb/${hid}`, ud);
      toast.success('HAWB saved');
    } catch (error) { toast.error('Failed to save HAWB'); }
  };

  const deleteHawb = async (hid) => {
    if (!window.confirm('Delete this HAWB?')) return;
    try {
      await api.delete(`/air-import/hawb/${hid}`);
      setHawbs(prev => prev.filter(h => h.id !== hid));
      toast.success('HAWB deleted');
    } catch (error) { toast.error('Failed to delete HAWB'); }
  };

  const toggleHawb = (hid) => setExpandedHawbs(prev => ({ ...prev, [hid]: !prev[hid] }));
  const fmtDate = (d) => d ? d.split('T')[0] : '';

  // Upload AWB PDF and auto-fill fields
  const handleAwbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (awbFileRef.current) awbFileRef.current.value = '';

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    setAwbUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/import/parse-awb', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success && res.data.data) {
        const awb = res.data.data;
        const filledFields = [];

        // Auto-fill form fields from parsed AWB data (only fill empty fields)
        setFormData(prev => {
          const updated = { ...prev };

          const tryFill = (formField, awbValue, label) => {
            if (awbValue && !updated[formField]) {
              updated[formField] = awbValue;
              filledFields.push(label);
            }
          };

          tryFill('mawb_no', awb.mawb_no, 'MAWB No');
          tryFill('carrier', awb.carrier, 'Carrier');
          tryFill('departure', awb.departure, 'Departure');
          tryFill('destination', awb.destination, 'Destination');
          tryFill('flight_no', awb.flight_no, 'Flight No');
          tryFill('connecting_flight', awb.connecting_flight, 'Connecting Flight');
          tryFill('freight', awb.freight, 'Freight');
          tryFill('package_count', awb.pieces, 'Package Count');
          tryFill('gross_weight_kg', awb.gross_weight_kg, 'Gross Weight KG');
          tryFill('chargeable_weight_kg', awb.chargeable_weight_kg, 'Chargeable Weight KG');
          tryFill('measurement_cbm', awb.volume_cbm, 'Volume CBM');

          // Fill memo with description + dimensions if available
          if (awb.description && !updated.memo) {
            let memoText = awb.description;
            if (awb.dimensions) memoText += '\nDimensions: ' + awb.dimensions;
            updated.memo = memoText;
            filledFields.push('Memo');
          }

          // Package type from AWB
          if (awb.pkg_type && updated.pkg_type === 'CARTON(S)') {
            updated.pkg_type = awb.pkg_type;
          }

          // Convert gross weight to LB if we have KG
          if (awb.gross_weight_kg && !updated.gross_weight_lb) {
            updated.gross_weight_lb = (parseFloat(awb.gross_weight_kg) * 2.20462).toFixed(2);
          }

          // Convert chargeable weight to LB if we have KG
          if (awb.chargeable_weight_kg && !updated.chargeable_weight_lb) {
            updated.chargeable_weight_lb = (parseFloat(awb.chargeable_weight_kg) * 2.20462).toFixed(2);
          }

          // Flight date → departure_date_time
          if (awb.flight_date && !updated.departure_date_time) {
            updated.departure_date_time = awb.flight_date;
            filledFields.push('Departure Date');
          }

          return updated;
        });

        setIsDirty(true);

        if (filledFields.length > 0) {
          toast.success(`AWB parsed! Filled: ${filledFields.join(', ')}`);
        } else {
          toast.info('AWB parsed but all fields already have values. No changes made.');
        }
      }
    } catch (error) {
      console.error('AWB upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to parse AWB file');
    } finally {
      setAwbUploading(false);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div className="shipment-page">
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if ((isNew || isDirty) && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/air-import');
        }}><FiArrowLeft /></button>
        <span>Home</span><span className="breadcrumb-sep">&gt;</span>
        <span>Air Import</span><span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.file_no || 'New'}</span>
      </div>

      <div className="shipment-tabs">
        {['Basic', 'Accounting', 'Doc Center'].map(tab => (
          <button key={tab} className={`shipment-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </div>

      <div className="shipment-header-bar">
        <div className="header-left">
          <span className="header-mbl"><span className="mbl-label">MAWB</span><span className="mbl-value">{formData.mawb_no || '-'}</span></span>
          <span className="header-customer">
            <span className="customer-label">{formData.departure || '-'} ({formData.departure_date_time ? fmtDate(formData.departure_date_time) : '-'})</span>
            <span style={{ margin: '0 4px', color: '#94a3b8' }}>▸</span>
            <span className="customer-value">{formData.destination || '-'} ({formData.arrival_date_time ? fmtDate(formData.arrival_date_time) : '-'})</span>
          </span>
        </div>
        <div className="header-right">
          <StatusSelector
            value={formData.status}
            options={['Pending', 'In Transit', 'Arrived', 'Delivered', 'Closed']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          <span className="header-route">✈ {formData.carrier || '-'} &middot; {formData.flight_no || '-'}</span>
          {formData.mawb_no && (
            <button
              className="header-tools-btn"
              style={{ background: '#2563eb', color: '#fff', marginRight: '8px' }}
              onClick={() => {
                const tracking = getTrackingUrl(formData.carrier || formData.flight_no || '', formData.mawb_no, 'air-import');
                if (tracking) window.open(tracking.url, '_blank');
              }}
              title="Track shipment online"
            >
              <FiNavigation size={14} style={{ marginRight: '4px' }} /> Track
            </button>
          )}
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
              Tools <FiChevronDown />
            </button>
            {showTools && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
                marginTop: '4px', overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-import/${shipmentId}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-import/${shipmentId}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-import/${shipmentId}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/air-import/${shipmentId}/authority-to-make-entry`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiFileText size={15} style={{ color: '#7c3aed' }} /> Authority to Make Entry
                </button>
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '2px 0' }}></div>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Actions</div>
                <button onClick={() => { setShowTools(false); setShowUploadModal(true); }} style={toolsMenuItemStyle}>
                  <FiUpload size={15} style={{ color: '#2563eb' }} /> Upload Document (Update)
                </button>
                <button onClick={async () => {
                  setShowTools(false);
                  if (!window.confirm('Copy this shipment? A new shipment will be created with same customer, delivery info, and charges (MBL/HBL/Container left blank).')) return;
                  try {
                    const res = await api.post(`/air-import/${shipmentId}/copy`);
                    toast.success(`Shipment copied! New File No: ${res.data.file_no}`);
                    navigate(`/air-import/${res.data.id}`);
                  } catch (err) {
                    toast.error('Failed to copy shipment');
                  }
                }} style={toolsMenuItemStyle}>
                  <FiCopy size={15} style={{ color: '#6366f1' }} /> Copy Shipment
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== BASIC TAB ==================== */}
      {activeTab === 'Basic' && (
        <div className="oi-basic-content">
          <form onSubmit={handleSave} className="shipment-form">
            <div className="oi-mbl-section">

              <div className="form-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>General Information</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="file" ref={awbFileRef} accept=".pdf" onChange={handleAwbUpload} style={{ display: 'none' }} />
                  <button type="button" onClick={() => awbFileRef.current?.click()} disabled={awbUploading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                      background: awbUploading ? '#94a3b8' : '#2563eb', color: '#fff',
                      border: 'none', borderRadius: '4px', cursor: awbUploading ? 'wait' : 'pointer',
                      transition: 'background 0.15s',
                    }}
                    title="Upload Air Waybill PDF to auto-fill shipment fields"
                  >
                    <FiUpload size={12} /> {awbUploading ? 'Parsing...' : 'Upload AWB'}
                  </button>
                </div>
              </div>
              <div className="shipment-form-grid">
                <FG label="File No." value={formData.file_no} disabled />
                <FG label="MAWB No." name="mawb_no" value={formData.mawb_no} onChange={handleChange} required />
                <FGSelect label="AWB Type" name="awb_type" value={formData.awb_type} onChange={handleChange} options={[['NORMAL','NORMAL'],['MASTER ONLY','MASTER ONLY']]} />
                <FGDate label="Post Date" name="post_date" value={formData.post_date} onChange={handleChange} />
                <div className="shipment-form-group"><label>Oversea Agent</label>
                  <TradePartnerAutocomplete value={formData.oversea_agent} tpTypes="Customer,Vendor" placeholder="Search oversea agent..."
                    onChange={(val) => setFormData(prev => ({ ...prev, oversea_agent: val }))} /></div>
                <FG label="Carrier" name="carrier" value={formData.carrier} onChange={handleChange} />
                <FG label="AWB Acct. Carrier" name="awb_acct_carrier" value={formData.awb_acct_carrier} onChange={handleChange} />
                <FG label="Co-loader" name="co_loader" value={formData.co_loader} onChange={handleChange} />
                <div className="shipment-form-group"><label>Trucker</label>
                  <TradePartnerAutocomplete value={formData.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                    onChange={(val) => setFormData(prev => ({ ...prev, trucker: val }))} /></div>
                <FG label="OP" name="op" value={formData.op} onChange={handleChange} />
                <FGCheck label="Direct Master" name="direct_master" checked={formData.direct_master} onChange={handleChange} />
              </div>

              <div className="form-section-header">Flight Information</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group"><label>Departure</label>
                  <TradePartnerAutocomplete value={formData.departure} placeholder="Departure..."
                    onChange={(val) => setFormData(prev => ({ ...prev, departure: val }))} /></div>
                <FG label="Departure Date/Time" name="departure_date_time" value={formData.departure_date_time} onChange={handleChange} type="datetime-local" />
                <FG label="Flight No." name="flight_no" value={formData.flight_no} onChange={handleChange} />
                <FG label="Connecting Flight" name="connecting_flight" value={formData.connecting_flight} onChange={handleChange} />
                <div className="shipment-form-group"><label>Destination</label>
                  <TradePartnerAutocomplete value={formData.destination} placeholder="Destination..."
                    onChange={(val) => setFormData(prev => ({ ...prev, destination: val }))} /></div>
                <FG label="Arrival Date/Time" name="arrival_date_time" value={formData.arrival_date_time} onChange={handleChange} type="datetime-local" required />
                <div className="shipment-form-group"><label>Freight Location</label>
                  <TradePartnerAutocomplete value={formData.freight_location} placeholder="Freight location..."
                    onChange={(val) => setFormData(prev => ({ ...prev, freight_location: val }))} /></div>
                <FGDate label="Storage Start Date" name="storage_start_date" value={formData.storage_start_date} onChange={handleChange} />
              </div>

              <div className="form-section-header">Cargo</div>
              <div className="shipment-form-grid">
                <div className="shipment-form-group">
                  <label>Package</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input type="number" name="package_count" value={formData.package_count} onChange={handleChange} style={{ flex: '0 0 70px', padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <select name="pkg_type" value={formData.pkg_type} onChange={handleChange} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                      <option>CARTON(S)</option><option>PALLET(S)</option><option>PACKAGE(S)</option><option>PIECE(S)</option><option>DRUM(S)</option>
                    </select>
                  </div>
                </div>
                <div className="shipment-form-group">
                  <label>Gross Weight</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input type="number" name="gross_weight_kg" value={formData.gross_weight_kg} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                    <input type="number" name="gross_weight_lb" value={formData.gross_weight_lb} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>LB</span>
                  </div>
                </div>
                <div className="shipment-form-group">
                  <label>Chargeable Weight</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input type="number" name="chargeable_weight_kg" value={formData.chargeable_weight_kg} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                    <input type="number" name="chargeable_weight_lb" value={formData.chargeable_weight_lb} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>LB</span>
                  </div>
                </div>
                <div className="shipment-form-group">
                  <label>Volume Weight / Measurement</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input type="number" name="volume_weight_kg" value={formData.volume_weight_kg} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                    <input type="number" name="measurement_cbm" value={formData.measurement_cbm} onChange={handleChange} placeholder="0" style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>CBM</span>
                  </div>
                </div>
              </div>

              <div className="form-section-header">Freight & Terms</div>
              <div className="shipment-form-grid">
                <FGSelect label="Freight" name="freight" value={formData.freight} onChange={handleChange} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                <FG label="Incoterms" name="incoterms" value={formData.incoterms} onChange={handleChange} />
                <div className="shipment-form-group">
                  <label>Service Term</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <select name="service_term_from" value={formData.service_term_from} onChange={handleChange} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                      <option value="AIRPORT">AIRPORT</option><option value="DOOR">DOOR</option>
                    </select>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>~</span>
                    <select name="service_term_to" value={formData.service_term_to} onChange={handleChange} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                      <option value="AIRPORT">AIRPORT</option><option value="DOOR">DOOR</option>
                    </select>
                  </div>
                </div>
                <FGSelect label="Cargo Type" name="cargo_type" value={formData.cargo_type} onChange={handleChange} options={[['GENERAL CARGO','GENERAL CARGO'],['HAZ','HAZ'],['PER','PER']]} />
                <FGRadio label="Stackable" name="stackable" value={formData.stackable} onChange={handleChange} />
                <FG label="Business Referred By" name="business_referred_by" value={formData.business_referred_by} onChange={handleChange} />
                <FGSelect label="Status" name="status" value={formData.status} onChange={handleChange} options={[['Pending','Pending'],['In Transit','In Transit'],['Arrived','Arrived'],['Delivered','Delivered'],['Closed','Closed']]} />
              </div>

              <div className="form-section-header">Memo</div>
              <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="shipment-form-group">
                  <textarea name="memo" value={formData.memo} onChange={handleChange} rows={3}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                </div>
              </div>

              <div className="shipment-form-actions">
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : 'Save MAWB'}</button>
              </div>
            </div>

            {/* ==================== HAWB Section ==================== */}
            <div className="oi-hbl-section">
              <div className="oi-hbl-header">
                <span className="oi-hbl-title">House AWB ({hawbs.length})</span>
                <button type="button" className="btn-add-hbl" onClick={addHawb} disabled={isNew} title={isNew ? 'Save shipment first' : ''}><FiPlus size={13} /> Add HAWB</button>
              </div>

              {hawbs.length === 0 && (
                <div className="oi-hbl-empty">No House AWB added yet. Click "+ Add HAWB" to create one.</div>
              )}

              {hawbs.map((hawb) => (
                <div className="oi-hbl-card" key={hawb.id}>
                  <div className="oi-hbl-card-header" onClick={() => toggleHawb(hawb.id)}>
                    <div className="oi-hbl-card-info">
                      <span className="oi-hbl-no">{hawb.hawb_no || '(No HAWB No.)'}</span>
                      <span className="oi-hbl-customer">{hawb.customer || ''}</span>
                      {hawb.op && <span className="oi-hbl-op">OP: {hawb.op}</span>}
                    </div>
                    <div className="oi-hbl-card-actions">
                      <button type="button" className="btn-delete-hbl" onClick={(e) => { e.stopPropagation(); deleteHawb(hawb.id); }}><FiTrash2 size={13} /></button>
                      {expandedHawbs[hawb.id] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </div>
                  </div>

                  {expandedHawbs[hawb.id] && (
                    <div className="oi-hbl-card-body">
                      <div className="form-section-header">General</div>
                      <div className="shipment-form-grid">
                        <HF label="HAWB No." name="hawb_no" hbl={hawb} onChange={handleHawbChange} required />
                        <HF label="Quotation No." name="quotation_no" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="HSN" name="hsn" hbl={hawb} onChange={handleHawbChange} />
                        <div className="shipment-form-group"><label>Shipper</label>
                          <TradePartnerAutocomplete value={hawb.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                            onChange={(val) => setHawbField(hawb.id, 'shipper', val)} /></div>
                        <div className="shipment-form-group"><label>Consignee</label>
                          <TradePartnerAutocomplete value={hawb.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                            onChange={(val) => setHawbField(hawb.id, 'consignee', val)} /></div>
                        <div className="shipment-form-group"><label>Notify</label>
                          <TradePartnerAutocomplete value={hawb.notify} tpTypes="Customer,Vendor" placeholder="Search notify..."
                            onChange={(val) => setHawbField(hawb.id, 'notify', val)} /></div>
                        <div className="shipment-form-group"><label>Customer</label>
                          <TradePartnerAutocomplete value={hawb.customer} placeholder="Search customer..."
                            onChange={(val) => setHawbField(hawb.id, 'customer', val)} /></div>
                        <div className="shipment-form-group"><label>Bill To</label>
                          <TradePartnerAutocomplete value={hawb.bill_to} placeholder="Search bill to..."
                            onChange={(val) => setHawbField(hawb.id, 'bill_to', val)} /></div>
                        <div className="shipment-form-group"><label>Customs Broker</label>
                          <TradePartnerAutocomplete value={hawb.customs_broker} tpTypes="Customer,Vendor" placeholder="Search customs broker..."
                            onChange={(val) => setHawbField(hawb.id, 'customs_broker', val)} /></div>
                        <HF label="Sales" name="sales" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="OP" name="op" hbl={hawb} onChange={handleHawbChange} />
                      </div>

                      <div className="form-section-header">Delivery</div>
                      <div className="shipment-form-grid">
                        <div className="shipment-form-group"><label>Freight Location</label>
                          <TradePartnerAutocomplete value={hawb.freight_location} placeholder="Freight location..."
                            onChange={(val) => setHawbField(hawb.id, 'freight_location', val)} /></div>
                        <div className="shipment-form-group"><label>Final Destination</label>
                          <TradePartnerAutocomplete value={hawb.final_destination} placeholder="Final destination..."
                            onChange={(val) => setHawbField(hawb.id, 'final_destination', val)} /></div>
                        <HFDate label="Final ETA" name="final_eta" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Delivery Location" name="delivery_location" hbl={hawb} onChange={handleHawbChange} />
                        <div className="shipment-form-group"><label>Trucker</label>
                          <TradePartnerAutocomplete value={hawb.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                            onChange={(val) => setHawbField(hawb.id, 'trucker', val)} /></div>
                        <HFDate label="Last Free Day" name="last_free_day" hbl={hawb} onChange={handleHawbChange} />
                        <HFDate label="Storage Start Date" name="storage_start_date" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Sales Type" name="sales_type" hbl={hawb} onChange={handleHawbChange} />
                        <HFSelect label="Freight" name="freight" hbl={hawb} onChange={handleHawbChange} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                      </div>

                      <div className="form-section-header">Cargo</div>
                      <div className="shipment-form-grid">
                        <div className="shipment-form-group">
                          <label>Package</label>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input type="number" name="package_count" value={hawb.package_count || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: '0 0 60px', padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <select name="pkg_type" value={hawb.pkg_type || 'CARTON(S)'} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                              <option>CARTON(S)</option><option>PALLET(S)</option><option>PACKAGE(S)</option><option>PIECE(S)</option>
                            </select>
                          </div>
                        </div>
                        <div className="shipment-form-group">
                          <label>Gross Weight</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input type="number" name="gross_weight_kg" value={hawb.gross_weight_kg || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                            <input type="number" name="gross_weight_lb" value={hawb.gross_weight_lb || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>LB</span>
                          </div>
                        </div>
                        <div className="shipment-form-group">
                          <label>Chargeable Weight</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input type="number" name="chargeable_weight_kg" value={hawb.chargeable_weight_kg || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                            <input type="number" name="chargeable_weight_lb" value={hawb.chargeable_weight_lb || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>LB</span>
                          </div>
                        </div>
                        <div className="shipment-form-group">
                          <label>Volume Weight / CBM</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input type="number" name="volume_weight_kg" value={hawb.volume_weight_kg || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>KG</span>
                            <input type="number" name="measurement_cbm" value={hawb.measurement_cbm || ''} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }} />
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>CBM</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-section-header">Customs & Release</div>
                      <div className="shipment-form-grid">
                        <HF label="Entry No." name="entry_no" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="IT No." name="it_no" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Class of Entry" name="class_of_entry" hbl={hawb} onChange={handleHawbChange} />
                        <HFDate label="IT Date" name="it_date" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="IT Issued Location" name="it_issued_location" hbl={hawb} onChange={handleHawbChange} />
                        <HFCheck label="Frt. Released" name="freight_released" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Released By" name="released_by" hbl={hawb} onChange={handleHawbChange} />
                        <HFDate label="C. Released Date" name="c_released_date" hbl={hawb} onChange={handleHawbChange} />
                        <HFDate label="Door Delivered" name="door_delivered" hbl={hawb} onChange={handleHawbChange} />
                        <HF label="Cargo Released To" name="cargo_released_to" hbl={hawb} onChange={handleHawbChange} />
                      </div>

                      <div className="form-section-header">Terms</div>
                      <div className="shipment-form-grid">
                        <HFSelect label="Ship Type" name="ship_type" hbl={hawb} onChange={handleHawbChange} options={[['NORMAL','NORMAL'],['DIRECT','DIRECT']]} />
                        <HF label="Incoterms" name="incoterms" hbl={hawb} onChange={handleHawbChange} />
                        <div className="shipment-form-group">
                          <label>Service Term</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <select name="service_term_from" value={hawb.service_term_from || 'AIRPORT'} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                              <option value="AIRPORT">AIRPORT</option><option value="DOOR">DOOR</option>
                            </select>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>~</span>
                            <select name="service_term_to" value={hawb.service_term_to || 'AIRPORT'} onChange={(e) => handleHawbChange(hawb.id, e)} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', height: '30px' }}>
                              <option value="AIRPORT">AIRPORT</option><option value="DOOR">DOOR</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="form-section-header">Description & Remarks</div>
                      <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <div className="shipment-form-group">
                          <label>Customer Ref / P.O. No.</label>
                          <input type="text" name="customer_ref_po" value={hawb.customer_ref_po || ''} onChange={(e) => handleHawbChange(hawb.id, e)} />
                        </div>
                        <div className="shipment-form-group" />
                        <div className="shipment-form-group">
                          <label>Mark</label>
                          <textarea name="mark" value={hawb.mark || ''} onChange={(e) => handleHawbChange(hawb.id, e)} rows={3}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }} />
                        </div>
                        <div className="shipment-form-group">
                          <label>Description</label>
                          <textarea name="description" value={hawb.description || ''} onChange={(e) => handleHawbChange(hawb.id, e)} rows={3}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }} />
                        </div>
                      </div>
                      <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                        <div className="shipment-form-group">
                          <label>Remark</label>
                          <textarea name="remark" value={hawb.remark || ''} onChange={(e) => handleHawbChange(hawb.id, e)} rows={2}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }} />
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
                        <button type="button" className="btn-save" onClick={() => saveHawb(hawb.id)}>Save HAWB</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      )}

      {activeTab === 'Accounting' && shipmentId && <AccountingTab shipmentId={shipmentId} shipmentType="air_import" />}
      {activeTab === 'Accounting' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage accounting.</div>}
      {activeTab === 'Doc Center' && shipmentId && <DocCenterTab shipmentId={shipmentId} shipmentType="air-import" />}
      {activeTab === 'Doc Center' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to access documents.</div>}

      {/* Upload Document Modal for updating existing shipment */}
      {showUploadModal && shipmentId && (
        <ImportShipmentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          defaultType="air-import"
          existingShipment={{ id: shipmentId, type: 'air-import' }}
          onUpdated={() => {
            loadShipment(shipmentId);
          }}
        />
      )}
    </div>
  );
};

// Helper Components
const FG = ({ label, name, value, onChange, required, disabled, type = 'text' }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type={type} name={name} value={value || ''} onChange={onChange} disabled={disabled} />
  </div>
);
const FGDate = ({ label, name, value, onChange, required }) => (
  <div className="shipment-form-group"><label className={required ? 'required' : ''}>{label}</label><input type="date" name={name} value={value || ''} onChange={onChange} /></div>
);
const FGCheck = ({ label, name, checked, onChange }) => (
  <div className="shipment-form-group"><label>{label}</label><div style={{ paddingTop: '4px' }}><input type="checkbox" name={name} checked={checked} onChange={onChange} /></div></div>
);
const FGSelect = ({ label, name, value, onChange, options }) => (
  <div className="shipment-form-group"><label>{label}</label><select name={name} value={value || ''} onChange={onChange}>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
);
const FGRadio = ({ label, name, value, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <div style={{ paddingTop: '4px', display: 'flex', gap: '12px', fontSize: '12px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
        <input type="radio" name={name} checked={!!value} onChange={() => onChange({ target: { name, value: true, type: 'radio' } })} /> Yes
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
        <input type="radio" name={name} checked={!value} onChange={() => onChange({ target: { name, value: false, type: 'radio' } })} /> No
      </label>
    </div>
  </div>
);

// HAWB field helpers
const HF = ({ label, name, hbl, onChange, required }) => (
  <div className="shipment-form-group"><label className={required ? 'required' : ''}>{label}</label><input type="text" name={name} value={hbl[name] || ''} onChange={(e) => onChange(hbl.id, e)} /></div>
);
const HFDate = ({ label, name, hbl, onChange }) => (
  <div className="shipment-form-group"><label>{label}</label><input type="date" name={name} value={hbl[name] ? hbl[name].split('T')[0] : ''} onChange={(e) => onChange(hbl.id, e)} /></div>
);
const HFCheck = ({ label, name, hbl, onChange }) => (
  <div className="shipment-form-group"><label>{label}</label><div style={{ paddingTop: '4px' }}><input type="checkbox" name={name} checked={!!hbl[name]} onChange={(e) => onChange(hbl.id, e)} /></div></div>
);
const HFSelect = ({ label, name, hbl, onChange, options }) => (
  <div className="shipment-form-group"><label>{label}</label><select name={name} value={hbl[name] || ''} onChange={(e) => onChange(hbl.id, e)}>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
);

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default AirImportDetail;
